import React, { Component } from 'react';
import { connect } from 'react-redux';
import querystring from 'querystring';
import { get, debounce } from 'lodash';

import { logActivity } from '_analytics/analytics-logger';
import { ACTIVITY_TYPES } from '_enums/activity-types';
import { fetchMedia } from '_redux/actioncreators/media';
import { fetchEvents } from '_redux/actioncreators/events';

import 'scss/components/_gallery.scss';

import Grid from '../gallery/Grid';
import Filters from '../gallery/Filters';

class Gallery extends Component {
  constructor(props) {
    super();

    const parsedQuery = querystring.parse(location.search.slice(1));

    this.state = {
      searchTerm: get(parsedQuery, 'searchTerm', ''),
      selectedEvent: null,
      resizeBool: false,
      pageNumber: 0,
      showBanner: true,
    };

    // Don't show the banner if we load the page with a search term.
    if (this.state.searchTerm !== '') {
      this.state.showBanner = false;
    }

    props.fetchMedia({
      cultureCode: props.cultureCode,
      pageNumber: this.state.pageNumber,
      searchTerm: this.state.searchTerm,
    });

    props.fetchEvents(props.cultureCode);

    // Log an activity if the user arrived at the gallery page via an email.
    const referer = get(parsedQuery, 'referer', '');
    if (referer === 'email') {
      this.logEmailRefererActivity(this.state.searchTerm, props.cultureCode);
    }
  }

  componentDidMount() {
    window.addEventListener('resize', debounce(this.handleResize, 100));
    window.addEventListener('orientationchange', this.handleResize);
    window.addEventListener('scroll', debounce(this.handleScroll, 5));
  }

  render() {
    return (
      <div className="thunderbolt__page" onKeyDown={this.handleSearch} ref={this.galleryContainer}>
        <div className="thunderbolt__gallery">
          <Filters
            searchTerm={this.state.searchTerm}
            handleSearch={this.handleSearch}
            handleSearchChange={this.handleSearchChange}
            handleEventSelected={this.handleEventSelected}
            {...this.props}>
          </Filters>

          <Grid
            getSplicedMediaItemIndex={this.getSplicedMediaItemIndex}
            showBanner={this.state.showBanner}
            {...this.props}>
          </Grid>
        </div>
      </div>
    );
  }

  getSplicedMediaItemIndex = () => {
    if (this.state.showBanner === false) {
      return 0;
    }

    if (window.matchMedia('(max-width: 768px)').matches) {
      return 1;
    }

    if (window.matchMedia('(max-width: 991px)').matches) {
      return 2;
    }

    if (window.matchMedia('(max-width: 1400px)').matches) {
      return 3;
    }

    return 4;
  }

  handleResize = () => {
    // this is to trigger a re-render so that the slicing of media items
    // based on screen size in the render function works correctly..
    // not sure how I feel about this
    this.setState({
      resizeBool: !this.state.resizeBool,
    });
  }

  handleScroll = () => {
    if (this.props.media.content.canLoadMore === false || this.props.media.contentLoading === true) {
      return;
    }

    const html = document.documentElement;
    if ((html.scrollTop + html.clientHeight) >= (html.scrollHeight / 1.5) && html.scrollHeight > html.clientHeight) {
      this.setState({
        pageNumber: this.state.pageNumber + 1,
      }, this.getMediaItems);
    }
  }

  handleSearch = (event) => {
    let showBanner = this.state.showBanner;

    if (event.keyCode === 13 || event.type === 'click') {
      if (this.state.searchTerm !== '') {
        showBanner = false;
      } else {
        showBanner = true;
      }

      this.setState({
        showBanner,
      }, this.getMediaItems);

      this.updateHistoryState();

      this.logSearchActivity(this.state.searchTerm, this.state.selectedEvent);
    }
  };

  handleSearchChange = (event) => {
    this.setState({ searchTerm: event.target.value, pageNumber: 0 });
  }

  handleEventSelected = (item) => {
    this.setState({
      selectedEvent: item,
      pageNumber: 0,
    }, this.getMediaItems);

    this.logSearchActivity(this.state.searchTerm, item);
  }

  getMediaItems = () => {
    let location = null;
    if (this.state.selectedEvent !== null) {
      location = this.state.selectedEvent.customData;
    }

    this.props.fetchMedia({
      cultureCode: this.props.cultureCode,
      searchTerm: this.state.searchTerm,
      ...location,
      pageNumber: this.state.pageNumber,
    });
  }

  updateHistoryState() {
    // Construct a query params object from the current state.
    const queryParams = {
      searchTerm: this.state.searchTerm,
    };

    // Remove any empty values from the query object.
    Array.forEach(Object.keys(queryParams), (key) => {
      if (queryParams[key] === '') {
        delete queryParams[key];
      }
    });

    // Construct the new URL from the query params.
    let newUrl = window.location.pathname;
    const queryString = querystring.stringify(queryParams);
    if (queryString !== '') {
      newUrl = `${newUrl}?${queryString}`;
    }

    // Update the browser history state with the new URL.
    history.replaceState(null, null, newUrl);
  }

  logSearchActivity(searchTerm, eventItem) {
    const activityParams = {
      activityType: ACTIVITY_TYPES.PERFORM_GALLERY_SEARCH,
      activityCulture: this.props.cultureCode,
    };

    if (searchTerm) {
      activityParams.activityValue1 = searchTerm;
    }

    if (eventItem) {
      activityParams.activityValue2 = eventItem.customData.latitude;
      activityParams.activityValue3 = eventItem.customData.longitude;
    }

    logActivity(activityParams);
  }

  logEmailRefererActivity(searchTerm, cultureCode) {
    const activityParams = {
      activityType: ACTIVITY_TYPES.GALLERY_EMAIL_REFERER,
      activityCulture: cultureCode,
    };

    if (searchTerm) {
      activityParams.activityValue1 = searchTerm;
    }

    logActivity(activityParams);
  }
}

const mapStateToProps = state => ({
  page: state.app.page,
  media: state.app.media,
  events: state.app.events,
});

const mapDispatchToProps = dispatch => ({
  fetchMedia: params => dispatch(fetchMedia(params)),
  fetchEvents: cultureCode => dispatch(fetchEvents({ cultureCode })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Gallery);
